import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, formatDate, daysLabel } from '../utils/api';
import './Dashboard.css';

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/payments/summary')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-state">Loading dashboard…</div>;
  if (!data)   return <div className="empty-state">Could not load dashboard.</div>;

  const {
    totalOutstanding, activeProjectCount,
    receivedThisMonth, receivedLastMonth,
    overdueProjects, vendorOutstanding, vendorCount,
    subscriptionRenewals, paidPendingCollection
  } = data;

  // Overdue buckets
  const under30 = overdueProjects.filter(p => p.days_overdue <= 30);
  const under60 = overdueProjects.filter(p => p.days_overdue > 30 && p.days_overdue <= 60);
  const over60  = overdueProjects.filter(p => p.days_overdue > 60);

  // Subscription buckets
  const sub7  = subscriptionRenewals.filter(s => s.days_until_renewal <= 7);
  const sub20 = subscriptionRenewals.filter(s => s.days_until_renewal > 7  && s.days_until_renewal <= 20);
  const sub45 = subscriptionRenewals.filter(s => s.days_until_renewal > 20 && s.days_until_renewal <= 45);

  return (
    <div className="page-content">

      {/* KPI Row */}
      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card accent">
          <div className="kpi-label">Total Outstanding</div>
          <div className="kpi-value red">{formatINR(totalOutstanding)}</div>
          <div className="kpi-sub">across <strong>{activeProjectCount}</strong> active projects</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Received This Month</div>
          <div className="kpi-value green">{formatINR(receivedThisMonth)}</div>
          <div className="kpi-sub">vs <strong>{formatINR(receivedLastMonth)}</strong> last month</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Overdue Amount</div>
          <div className="kpi-value danger">
            {formatINR(overdueProjects.reduce((s, p) => s + p.outstanding, 0))}
          </div>
          <div className="kpi-sub"><strong>{overdueProjects.length}</strong> project{overdueProjects.length !== 1 ? 's' : ''} past due</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Vendor Payouts Due</div>
          <div className="kpi-value">{formatINR(vendorOutstanding)}</div>
          <div className="kpi-sub">across <strong>{vendorCount}</strong> vendors</div>
        </div>
      </div>

      {/* Overdue Ageing */}
      <div className="dash-section-label">Project Overdue Ageing</div>
      <div className="dash-three-col">
        <OverdueCard
          title="Under 30 Days"
          colorClass="amber"
          items={under30}
          total={under30.reduce((s, p) => s + p.outstanding, 0)}
          onClickProject={id => navigate(`/projects/${id}`)}
        />
        <OverdueCard
          title="Under 60 Days"
          colorClass="orange"
          items={under60}
          total={under60.reduce((s, p) => s + p.outstanding, 0)}
          onClickProject={id => navigate(`/projects/${id}`)}
        />
        <OverdueCard
          title="Over 60 Days"
          colorClass="red"
          items={over60}
          total={over60.reduce((s, p) => s + p.outstanding, 0)}
          onClickProject={id => navigate(`/projects/${id}`)}
        />
      </div>

      {/* Subscription Renewals */}
      <div className="dash-section-label">Subscription Renewals</div>
      <div className="dash-four-col">
        <SubCard
          title="Renewing in 7 Days"
          colorClass="red"
          items={sub7}
          onClickSub={() => navigate('/subscriptions')}
          renderDays={s => `${s.days_until_renewal}d`}
        />
        <SubCard
          title="Renewing in 20 Days"
          colorClass="orange"
          items={sub20}
          onClickSub={() => navigate('/subscriptions')}
          renderDays={s => `${s.days_until_renewal}d`}
        />
        <SubCard
          title="Renewing in 45 Days"
          colorClass="amber"
          items={sub45}
          onClickSub={() => navigate('/subscriptions')}
          renderDays={s => `${s.days_until_renewal}d`}
        />
        <SubCard
          title="Paid — Pending"
          colorClass="blue"
          items={paidPendingCollection}
          onClickSub={() => navigate('/subscriptions')}
          renderDays={s => s.we_paid_date ? formatDate(s.we_paid_date) : '—'}
          isPending
        />
      </div>

    </div>
  );
}

/* ---- Overdue Card ---- */
function OverdueCard({ title, colorClass, items, total, onClickProject }) {
  return (
    <div className={`dash-card overdue-card overdue-${colorClass}`}>
      <div className={`dash-card-head head-${colorClass}`}>
        <span className={`dash-col-title title-${colorClass}`}>{title}</span>
        <span className={`dash-col-total total-${colorClass}`}>
          {total > 0 ? formatINR(total) : '—'}
        </span>
      </div>
      <div className="dash-scroll-list">
        {items.length === 0
          ? <div className="dash-empty">
              <span className="dash-empty-icon">✓</span>
              No projects here
            </div>
          : items.map(p => (
            <div
              key={p.id}
              className="dash-list-item"
              onClick={() => onClickProject(p.id)}
            >
              <div className="dash-item-left">
                <div className="dash-item-name">{p.project_name}</div>
                <div className="dash-item-sub">{p.client_name}</div>
              </div>
              <div className="dash-item-right">
                <div className={`dash-item-amt total-${colorClass}`}>
                  {formatINR(p.outstanding)}
                </div>
                <div className={`dash-days-chip chip-${colorClass}`}>
                  {p.days_overdue}d overdue
                </div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

/* ---- Subscription Card ---- */
function SubCard({ title, colorClass, items, onClickSub, renderDays, isPending }) {
  return (
    <div className={`dash-card sub-card sub-${colorClass}`}>
      <div className={`dash-card-head head-${colorClass}`}>
        <span className={`dash-col-title title-${colorClass}`}>{title}</span>
        <span className={`dash-col-count count-${colorClass}`}>
          {items.length > 0 ? items.length : '—'}
        </span>
      </div>
      <div className="dash-scroll-list">
        {items.length === 0
          ? <div className="dash-empty">
              <span className="dash-empty-icon">{isPending ? '✓' : '🔔'}</span>
              {isPending ? 'All collected' : 'None due'}
            </div>
          : items.map(s => (
            <div key={s.id} className="dash-list-item" onClick={onClickSub}>
              <div className="dash-item-left">
                <div className="dash-item-name">{s.name}</div>
                <div className="dash-item-sub">{s.client_name}</div>
              </div>
              <div className="dash-item-right">
                <div className={`dash-item-amt total-${colorClass}`}>
                  {formatINR(s.client_cost + (s.client_cost * 0.18))}
                </div>
                <div className={`dash-days-chip chip-${colorClass}`}>
                  {renderDays(s)}
                </div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
